import React, { useState, useEffect } from 'react';
import { Box, VStack, Text, Input, Button, HStack, Badge, Spinner } from '@chakra-ui/react';
import { Field } from '@/components/ui/field';
import { ScannedQRData, StoreProduct, SelectedProduct } from '@/types/payment';
import { getStoreProducts } from '@/api/wallet';
import { ProductSelectBottomSheet } from './ProductSelectBottomSheet';

interface AmountInputContentProps {
  qrData: ScannedQRData;
  onNext: (amount: number, selectedProducts: SelectedProduct[]) => void;
  onCancel: () => void;
  onBack?: () => void;
}

export const AmountInputContent: React.FC<AmountInputContentProps> = ({
  qrData,
  onNext,
  onCancel,
  onBack,
}) => {
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [isProductSheetOpen, setIsProductSheetOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  // ボトムシート内での一時選択状態（確定前）
  const [draftProducts, setDraftProducts] = useState<SelectedProduct[]>([]);

  // 商品一覧
  const [products, setProducts] = useState<StoreProduct[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [productLoadError, setProductLoadError] = useState('');

  const isStoreQR = qrData.type === 'STORE';

  // STOREタイプのQRコードの場合、商品一覧を取得する
  useEffect(() => {
    if (!isStoreQR || !qrData.encodedValue) return;

    const loadProducts = async () => {
      setIsLoadingProducts(true);
      setProductLoadError('');
      try {
        const response = await getStoreProducts(qrData.encodedValue!);
        const mappedProducts: StoreProduct[] = response.products.map(p => ({
          id: p.productCode,
          name: p.productName,
          price: p.amount,
          productStatus: p.productStatus,
        }));
        setProducts(mappedProducts);
      } catch {
        setProductLoadError('商品一覧の取得に失敗しました');
      } finally {
        setIsLoadingProducts(false);
      }
    };

    loadProducts();
  }, [isStoreQR, qrData.encodedValue]);

  const hasProductSelection = selectedProducts.length > 0;
  const totalFromProducts = selectedProducts.reduce(
    (sum, sp) => sum + sp.product.price * sp.quantity,
    0
  );

  // 表示する金額：商品選択時は合計、未選択時は手入力値
  const displayAmount = hasProductSelection ? String(totalFromProducts) : amount;
  const effectiveAmount = hasProductSelection ? totalFromProducts : Number(amount);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (hasProductSelection) return;
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setAmount(value);
      setError('');
    }
  };

  const handleNext = () => {
    if (!displayAmount || effectiveAmount <= 0) {
      setError('金額を入力してください');
      return;
    }
    if (effectiveAmount > 1000000) {
      setError('金額は1Bunから1,000,000Bunの範囲で入力してください');
      return;
    }
    onNext(effectiveAmount, selectedProducts);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleNext();
    }
  };

  // 商品選択ボトムシートを開く（ドラフトに現在の選択を引き継ぐ）
  const handleOpenProductSheet = () => {
    setDraftProducts(selectedProducts);
    setIsProductSheetOpen(true);
  };

  // ドラフト内の商品数量を変更
  const handleDraftChangeQuantity = (product: StoreProduct, delta: number) => {
    setDraftProducts(prev => {
      const existing = prev.find(sp => sp.product.id === product.id);
      if (!existing) {
        if (delta > 0) return [...prev, { product, quantity: 1 }];
        return prev;
      }
      const newQty = existing.quantity + delta;
      if (newQty <= 0) return prev.filter(sp => sp.product.id !== product.id);
      return prev.map(sp => (sp.product.id === product.id ? { ...sp, quantity: newQty } : sp));
    });
  };

  // 商品選択を確定する
  const handleConfirmProducts = () => {
    setSelectedProducts(draftProducts);
    setError('');
    setIsProductSheetOpen(false);
  };

  // 商品選択をリセットする
  const handleResetProducts = () => {
    setSelectedProducts([]);
    setAmount('');
    setError('');
  };

  return (
    <VStack gap={6} w="full" p={4}>
      {/* 店舗情報 */}
      <Box w="full" bg="teal.50" borderRadius="lg" p={4} userSelect="none">
        <Text fontSize="sm" color="gray.600" mb={1}>
          支払先
        </Text>
        <Text fontSize="lg" fontWeight="bold" color="gray.800">
          {qrData.payeeName}
        </Text>
        {qrData.payeeDetail && (
          <Text fontSize="sm" color="gray.600" mt={1}>
            {qrData.payeeDetail}
          </Text>
        )}
      </Box>

      {/* 金額入力 */}
      <Field label="支払金額" required invalid={!!error} errorText={error}>
        <Input
          type="tel"
          placeholder="0"
          value={displayAmount}
          onChange={handleAmountChange}
          onKeyDown={handleKeyDown}
          readOnly={hasProductSelection}
          size="lg"
          fontSize="2xl"
          textAlign="right"
          autoFocus={!hasProductSelection}
          bg={hasProductSelection ? 'gray.50' : undefined}
          color={hasProductSelection ? 'gray.500' : undefined}
          cursor={hasProductSelection ? 'default' : undefined}
        />
      </Field>

      {/* 商品選択エリア（STOREタイプのQRのみ表示） */}
      {isStoreQR && (
        <Box w="full" userSelect="none">
          {isLoadingProducts ? (
            // 読み込み中
            <HStack justify="center" py={2}>
              <Spinner size="sm" color="teal.500" />
              <Text fontSize="sm" color="gray.500">
                商品を読み込み中...
              </Text>
            </HStack>
          ) : productLoadError ? (
            // 読み込みエラー
            <VStack gap={2}>
              <Text fontSize="sm" color="red.500">
                {productLoadError}
              </Text>
              <Button
                size="sm"
                variant="outline"
                colorPalette="teal"
                onClick={() => {
                  if (!qrData.encodedValue) return;
                  setIsLoadingProducts(true);
                  setProductLoadError('');
                  getStoreProducts(qrData.encodedValue)
                    .then(response => {
                      setProducts(
                        response.products.map(p => ({
                          id: p.productCode,
                          name: p.productName,
                          price: p.amount,
                          productStatus: p.productStatus,
                        }))
                      );
                    })
                    .catch(() => setProductLoadError('商品一覧の取得に失敗しました'))
                    .finally(() => setIsLoadingProducts(false));
                }}
              >
                再読み込み
              </Button>
            </VStack>
          ) : hasProductSelection ? (
            // 商品選択中：選択済み商品サマリー＋リセットボタン
            <VStack align="stretch" gap={2}>
              <HStack justify="space-between" align="center">
                <Text fontSize="xs" color="gray.600">
                  選択中の商品
                </Text>
                <Button size="xs" variant="ghost" colorPalette="red" onClick={handleResetProducts}>
                  選択をリセット
                </Button>
              </HStack>
              <Box bg="teal.50" borderRadius="md" p={3}>
                <VStack align="stretch" gap={1}>
                  {selectedProducts.map(sp => (
                    <HStack key={sp.product.id} justify="space-between">
                      <HStack gap={2}>
                        <Badge colorPalette="teal" size="sm">
                          ×{sp.quantity}
                        </Badge>
                        <Text fontSize="sm" color="gray.700">
                          {sp.product.name}
                        </Text>
                      </HStack>
                      <Text fontSize="sm" color="teal.700" fontWeight="medium">
                        ฿{(sp.product.price * sp.quantity).toLocaleString()}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>
              <Button
                size="sm"
                variant="outline"
                colorPalette="teal"
                onClick={handleOpenProductSheet}
              >
                商品を変更する
              </Button>
            </VStack>
          ) : (
            // 未選択：商品選択ボタン
            <Button
              w="full"
              size="md"
              variant="outline"
              colorPalette="teal"
              onClick={handleOpenProductSheet}
              disabled={products.length === 0}
            >
              商品を選択する
            </Button>
          )}
        </Box>
      )}

      {/* アクションボタン */}
      <VStack gap={2} w="full" mt={4} userSelect="none">
        <Button
          w="full"
          size="lg"
          colorPalette="teal"
          onClick={handleNext}
          disabled={!displayAmount || !!error}
        >
          次へ
        </Button>
        {/* {onBack && (
          <Button w="full" size="lg" variant="outline" onClick={onBack}>
            戻る
          </Button>
        )} */}
        <Button w="full" size="lg" variant="ghost" onClick={onCancel}>
          キャンセル
        </Button>
      </VStack>

      {/* 商品選択ボトムシート */}
      <ProductSelectBottomSheet
        isOpen={isProductSheetOpen}
        onClose={() => setIsProductSheetOpen(false)}
        products={products}
        selectedProducts={draftProducts}
        onChangeQuantity={handleDraftChangeQuantity}
        onConfirm={handleConfirmProducts}
      />
    </VStack>
  );
};
